import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Clock, Calendar, Users, ChevronLeft, ChevronRight, Play, Pause, Volume2, MessageSquare, FileText, TrendingUp, Mic } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({ total_calls: 0, completed_calls: 0, today_calls: 0, avg_duration: 0 });
  const [recentCalls, setRecentCalls] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDetails, setCallDetails] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);

  // Redirect admins to admin page, and regular users to their business dashboard
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      if (isAdmin) {
        navigate('/admin', { replace: true });
      } else if (user?.business_id) {
        navigate(`/business/${user.business_id}`, { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, isAdmin, user, navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [currentDate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, callsRes, calendarRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/business/my-calls?limit=10`),
        axios.get(`${API}/calendar/week?start_date=${getWeekStart(currentDate).toISOString().split('T')[0]}`)
      ]);
      
      setStats(statsRes.data);
      setRecentCalls(callsRes.data.calls || []);
      
      // Extract booked appointments from calendar
      const bookedSlots = [];
      (calendarRes.data.week || []).forEach(day => {
        (day.slots || []).forEach(slot => {
          if (slot.status === 'booked') {
            bookedSlots.push({
              ...slot,
              date: day.date,
              day_name: day.day_name
            });
          }
        });
      });
      setAppointments(bookedSlots);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const viewCallDetails = async (callId) => {
    setSelectedCall(callId);
    try {
      const res = await axios.get(`${API}/retell/call/${callId}`);
      setCallDetails(res.data);
    } catch (err) {
      console.error('Failed to fetch call details:', err);
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const handlePlayAudio = () => {
    if (callDetails?.recording_url) {
      if (isPlaying && audioRef) {
        audioRef.pause();
        setIsPlaying(false);
      } else {
        const audio = new Audio(callDetails.recording_url);
        audio.onended = () => setIsPlaying(false);
        audio.play();
        setAudioRef(audio);
        setIsPlaying(true);
      }
    }
  };

  const getKeyPoints = (callAnalysis) => {
    if (!callAnalysis) return [];
    const points = [];
    
    if (callAnalysis.call_summary) {
      points.push({ label: 'Summary', value: callAnalysis.call_summary });
    }
    if (callAnalysis.user_sentiment) {
      points.push({ label: 'Customer Mood', value: callAnalysis.user_sentiment });
    }
    if (callAnalysis.call_successful !== undefined) {
      points.push({ label: 'Outcome', value: callAnalysis.call_successful ? 'Successful' : 'Needs Follow-up' });
    }
    if (callAnalysis.custom_analysis_data) {
      const custom = callAnalysis.custom_analysis_data;
      if (custom.customer_name) points.push({ label: 'Customer', value: custom.customer_name });
      if (custom.service_requested) points.push({ label: 'Service', value: custom.service_requested });
      if (custom.appointment_time) points.push({ label: 'Appointment', value: custom.appointment_time });
    }
    
    return points;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">DASHBOARD</h1>
          <p className="text-muted-foreground text-sm">Front Desk Pro AI Dashboard</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-modern p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Total Calls</p>
                <p className="text-2xl font-bold text-foreground">{stats.total_calls}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-modern p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Completed</p>
                <p className="text-2xl font-bold text-green-400">{stats.completed_calls}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-modern p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Today</p>
                <p className="text-2xl font-bold text-purple-400">{stats.today_calls}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-modern p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Avg Duration</p>
                <p className="text-2xl font-bold text-orange-400">{formatDuration(stats.avg_duration)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar - Takes 2 columns */}
          <Card className="card-modern lg:col-span-2">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Weekly Schedule
              </h2>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setDate(newDate.getDate() - 7);
                  setCurrentDate(newDate);
                }}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())}>
                  Today
                </Button>
                <Button variant="ghost" size="sm" onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setDate(newDate.getDate() + 7);
                  setCurrentDate(newDate);
                }}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="p-4">
              {appointments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No appointments this week</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map((apt, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                      <div className="w-16 text-center">
                        <div className="text-xs text-muted-foreground">{apt.day_name?.slice(0,3)}</div>
                        <div className="text-lg font-bold text-foreground">{new Date(apt.date + 'T00:00:00').getDate()}</div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{apt.customer_name || 'Customer'}</span>
                          <span className="text-xs text-muted-foreground bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                            {formatTime(apt.time)}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">{apt.service_type || apt.notes || 'Service'}</div>
                        {apt.phone_number && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {apt.phone_number}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Recent Calls */}
          <Card className="card-modern">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Phone className="w-5 h-5" /> Recent Calls
              </h2>
            </div>
            
            <div className="divide-y divide-border">
              {recentCalls.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Phone className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No calls yet</p>
                </div>
              ) : (
                recentCalls.slice(0, 5).map((call) => (
                  <div
                    key={call.call_id}
                    className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => viewCallDetails(call.call_id)}
                    data-testid={`recent-call-${call.call_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${call.call_status === 'ended' ? 'bg-green-400' : 'bg-gray-400'}`} />
                        <div>
                          <p className="text-sm font-medium text-foreground">{call.from_number || 'Web Call'}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(call.start_timestamp)}</p>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDuration(call.duration_ms)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Call Details Section at Bottom */}
        {selectedCall && callDetails && (
          <Card className="card-modern" data-testid="call-details-section">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Conversation Details
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedCall(null); setCallDetails(null); }}>
                Close
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Call Info Bar */}
              <div className="flex flex-wrap items-center gap-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{callDetails.from_number || 'Web Call'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{formatDuration(callDetails.duration_ms)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{formatDate(callDetails.start_timestamp)}</span>
                </div>
                
                {/* Voice Playback Button */}
                {callDetails.recording_url && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handlePlayAudio}
                    className="ml-auto"
                    data-testid="play-recording-btn"
                  >
                    {isPlaying ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    {isPlaying ? 'Pause' : 'Listen'}
                    <Volume2 className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>

              {/* Key Points */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> Key Points
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {getKeyPoints(callDetails.call_analysis).map((point, i) => (
                    <div key={i} className="p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground">{point.label}</p>
                      <p className="text-sm font-medium text-foreground truncate">{point.value}</p>
                    </div>
                  ))}
                  {getKeyPoints(callDetails.call_analysis).length === 0 && (
                    <p className="text-muted-foreground text-sm col-span-4">No analysis available</p>
                  )}
                </div>
              </div>

              {/* Full Transcript (Expandable) */}
              <details className="group">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground flex items-center gap-2 hover:text-foreground transition-colors">
                  <FileText className="w-4 h-4" /> 
                  View Full Conversation
                  <span className="text-xs">(click to expand)</span>
                </summary>
                
                <div className="mt-3 p-4 bg-muted rounded-lg max-h-96 overflow-y-auto">
                  {callDetails.transcript ? (
                    typeof callDetails.transcript === 'string' ? (
                      <p className="text-foreground text-sm whitespace-pre-wrap">{callDetails.transcript}</p>
                    ) : (
                      <div className="space-y-3">
                        {callDetails.transcript.map((item, i) => (
                          <div 
                            key={i} 
                            className={`p-3 rounded-lg ${
                              item.role === 'user' 
                                ? 'bg-red-500/10 border-l-4 border-red-500 ml-0 mr-8' 
                                : 'bg-blue-500/10 border-l-4 border-blue-500 ml-8 mr-0'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {item.role === 'user' ? (
                                <Users className="w-4 h-4 text-red-400" />
                              ) : (
                                <Mic className="w-4 h-4 text-blue-400" />
                              )}
                              <span className={`text-xs font-medium ${item.role === 'user' ? 'text-red-400' : 'text-blue-400'}`}>
                                {item.role === 'user' ? 'CUSTOMER' : 'SOPHI'}
                              </span>
                            </div>
                            <p className="text-foreground text-sm">{item.content}</p>
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    <p className="text-muted-foreground text-sm">No transcript available</p>
                  )}
                </div>
              </details>

              {/* Audio Player (if recording available) */}
              {callDetails.recording_url && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
                    <Volume2 className="w-4 h-4" /> Audio Recording
                  </p>
                  <audio 
                    controls 
                    className="w-full" 
                    src={callDetails.recording_url}
                    data-testid="audio-player"
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
